"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var types_1 = require("./types");
var utils_1 = require("./utils");
var react_1 = require("react");
var FetchContext_1 = __importDefault(require("./FetchContext"));
exports.useFetchArgsDefaults = {
    customOptions: {
        retries: 0,
        timeout: 30000,
        path: '',
        url: '',
        interceptors: {},
        onAbort: function () { },
        onTimeout: function () { },
        onNewData: function (currData, newData) { return newData; },
        perPage: 0,
        cachePolicy: types_1.CachePolicies.CACHE_FIRST,
        cacheLife: 0
    },
    requestInit: { headers: {} },
    defaults: {
        data: undefined,
        loading: false
    },
    dependencies: undefined
};
// TODO: see if `Object.entries` is supported for IE
exports.defaults = Object.entries(exports.useFetchArgsDefaults).reduce(function (acc, _a) {
    var _b;
    var key = _a[0], value = _a[1];
    if (utils_1.isObject(value))
        return __assign(__assign({}, acc), value);
    return __assign(__assign({}, acc), (_b = {}, _b[key] = value, _b));
}, {});
var useField = function (field, urlOrOptions, optionsNoURLs) {
    var context = react_1.useContext(FetchContext_1.default);
    var contextOptions = context.options || {};
    return react_1.useMemo(function () {
        if (utils_1.isObject(urlOrOptions) && urlOrOptions[field])
            return urlOrOptions[field];
        if (utils_1.isObject(optionsNoURLs) && optionsNoURLs[field]) {
            return optionsNoURLs[field];
        }
        if (contextOptions[field])
            return contextOptions[field];
        return exports.defaults[field];
    }, [urlOrOptions, field, optionsNoURLs, contextOptions]);
};
function useFetchArgs(urlOrOptionsOrOverwriteGlobal, optionsNoURLsOrOverwriteGlobalOrDeps, deps) {
    var context = react_1.useContext(FetchContext_1.default);
    context.options = react_1.useMemo(function () {
        var overwriteGlobalOptions = (utils_1.isFunction(urlOrOptionsOrOverwriteGlobal) ? urlOrOptionsOrOverwriteGlobal : utils_1.isFunction(optionsNoURLsOrOverwriteGlobalOrDeps) && optionsNoURLsOrOverwriteGlobalOrDeps);
        if (!overwriteGlobalOptions)
            return context.options;
        // make a copy so we make sure not to modify the original context
        return overwriteGlobalOptions(__assign({}, context.options));
    }, [context.options, optionsNoURLsOrOverwriteGlobalOrDeps, urlOrOptionsOrOverwriteGlobal]);
    var urlOrOptions = urlOrOptionsOrOverwriteGlobal;
    var optionsNoURLs = optionsNoURLsOrOverwriteGlobalOrDeps;
    utils_1.invariant(!(utils_1.isObject(urlOrOptions) && utils_1.isObject(optionsNoURLs)), 'You cannot have a 2nd parameter of useFetch when your first argument is an object config.');
    var url = react_1.useMemo(function () {
        if (utils_1.isString(urlOrOptions) && urlOrOptions)
            return urlOrOptions;
        if (utils_1.isObject(urlOrOptions) && !!urlOrOptions.url)
            return urlOrOptions.url;
        if (context.url)
            return context.url;
        return exports.defaults.url;
    }, [context.url, urlOrOptions]);
    utils_1.invariant(!!url, 'The first argument of useFetch is required unless you have a global url setup like: <Provider url="https://example.com"></Provider>');
    var dependencies = react_1.useMemo(function () {
        if (Array.isArray(optionsNoURLsOrOverwriteGlobalOrDeps))
            return optionsNoURLsOrOverwriteGlobalOrDeps;
        if (Array.isArray(deps))
            return deps;
        return exports.defaults.dependencies;
    }, [optionsNoURLsOrOverwriteGlobalOrDeps, deps]);
    var data = useField('data', urlOrOptions, optionsNoURLs);
    var path = useField('path', urlOrOptions, optionsNoURLs);
    var timeout = useField('timeout', urlOrOptions, optionsNoURLs);
    var retries = useField('retries', urlOrOptions, optionsNoURLs);
    var onAbort = useField('onAbort', urlOrOptions, optionsNoURLs);
    var onTimeout = useField('onTimeout', urlOrOptions, optionsNoURLs);
    var onNewData = useField('onNewData', urlOrOptions, optionsNoURLs);
    var perPage = useField('perPage', urlOrOptions, optionsNoURLs);
    var cachePolicy = useField('cachePolicy', urlOrOptions, optionsNoURLs);
    var cacheLife = useField('cacheLife', urlOrOptions, optionsNoURLs);
    var loading = react_1.useMemo(function () {
        if (utils_1.isObject(urlOrOptions))
            return !!urlOrOptions.loading || Array.isArray(dependencies);
        if (utils_1.isObject(optionsNoURLs))
            return !!optionsNoURLs.loading || Array.isArray(dependencies);
        return exports.defaults.loading || Array.isArray(dependencies);
    }, [urlOrOptions, dependencies, optionsNoURLs]);
    var interceptors = react_1.useMemo(function () {
        var contextInterceptors = context.options && (context.options.interceptors || {});
        var final = __assign({}, contextInterceptors);
        if (utils_1.isObject(urlOrOptions) && utils_1.isObject(urlOrOptions.interceptors)) {
            if (urlOrOptions.interceptors.request)
                final.request = urlOrOptions.interceptors.request;
            if (urlOrOptions.interceptors.response)
                final.response = urlOrOptions.interceptors.response;
        }
        if (utils_1.isObject(optionsNoURLs) && utils_1.isObject(optionsNoURLs.interceptors)) {
            if (optionsNoURLs.interceptors.request)
                final.request = optionsNoURLs.interceptors.request;
            if (optionsNoURLs.interceptors.response)
                final.response = optionsNoURLs.interceptors.response;
        }
        return final;
    }, [context.options, urlOrOptions, optionsNoURLs]);
    var requestInit = react_1.useMemo(function () {
        var contextRequestInit = utils_1.pullOutRequestInit(context.options);
        var requestInitOptions = utils_1.isObject(urlOrOptions)
            ? urlOrOptions
            : utils_1.isObject(optionsNoURLs)
                ? optionsNoURLs
                : {};
        var requestInit = utils_1.pullOutRequestInit(requestInitOptions);
        return __assign(__assign(__assign({}, contextRequestInit), requestInit), { headers: __assign(__assign({}, contextRequestInit.headers), requestInit.headers) });
    }, [context.options, urlOrOptions, optionsNoURLs]);
    return {
        customOptions: {
            url: url,
            path: path,
            interceptors: interceptors,
            timeout: timeout,
            retries: retries,
            onAbort: onAbort,
            onTimeout: onTimeout,
            onNewData: onNewData,
            perPage: perPage,
            cachePolicy: cachePolicy,
            cacheLife: cacheLife
        },
        requestInit: requestInit,
        defaults: {
            data: data,
            loading: loading
        },
        dependencies: dependencies
    };
}
exports.default = useFetchArgs;
//# sourceMappingURL=useFetchArgs.js.map